import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ScrollView,
  Modal,
  Platform,
  TextInput,
  ActivityIndicator,
  Keyboard,
  useWindowDimensions,
  Alert,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import * as ImagePicker from 'expo-image-picker';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import YoutubeOAuthBar from '../components/YoutubeOAuthBar';
import {
  formatYoutubeUploadError,
  isYoutubeReconnectError,
  isYoutubeUploadLimitError,
} from '../utils/youtubeBinaryUpload';
import { uploadYoutubeVideoFile } from '../utils/youtubeFileUpload';
import {
  assertVideoUploadable,
  getExerciseVideoPickerOptions,
} from '../utils/videoUploadLimits';
import UserBreakVideosModal from '../components/UserBreakVideosModal';
import {
  formatCourseBreakMetaLine,
  formatCourseBreakWeaponTitle,
} from '../utils/breakDisplayFormat';
import {
  normalizeUserBreakVideoLinks,
  hasUserBreakVideos,
} from '../utils/userBreakVideoLinks';
import {
  fetchExistingUserBreakMeta,
  resolveBreakId,
  saveUserBreakWithVideo,
} from '../utils/userBreakUpload';
import { hasSuperUserAccess } from '../utils/sessionUser';

const GRADE_HEADER_ICON = require('../../assets/ChatGPT Image Jan 28, 2026, 12_32_42 AM.png');
const YELLOW_BELT_SYLLABUS_ICON = require('../../assets/belts images/ChatGPT Image Apr 10, 2026, 11_48_05 PM.png');
const GREEN_BELT_SYLLABUS_ICON = require('../../assets/belts images/Green belt image.png');
const RED_BELT_SYLLABUS_ICON = require('../../assets/belts images/Red belt image.png');
const BROWN_BELT_SYLLABUS_ICON = require('../../assets/belts images/Brown belt image.png');
const SHODAN_HO_SYLLABUS_ICON = require('../../assets/belts images/Shodan-ho belt image.png');
const FIRST_DEGREE_BELT_SYLLABUS_ICON = require('../../assets/belts images/1st degree belt image.png');
const SECOND_DEGREE_BELT_SYLLABUS_ICON = require('../../assets/belts images/2nd degree black belt image.png');

function getGradeBeltIconSource(gradeName) {
  if (gradeName === 'Yellow Belt') return YELLOW_BELT_SYLLABUS_ICON;
  if (gradeName === 'Green Belt') return GREEN_BELT_SYLLABUS_ICON;
  if (gradeName === 'Red Belt') return RED_BELT_SYLLABUS_ICON;
  if (gradeName === 'Brown Belt') return BROWN_BELT_SYLLABUS_ICON;
  if (gradeName === 'Shodan Ho' || gradeName === 'Shodan-ho')
    return SHODAN_HO_SYLLABUS_ICON;
  if (gradeName === '1st Degree Black Belt') return FIRST_DEGREE_BELT_SYLLABUS_ICON;
  if (gradeName === '2nd Degree Black Belt') return SECOND_DEGREE_BELT_SYLLABUS_ICON;
  return GRADE_HEADER_ICON;
}

const SYLLABUS_OMIT_GRADE_NAMES = new Set(['Beginner']);

const TABLE_MIN_WIDTH = 840;
const GRADE_TABLE_COLUMNS = [
  { key: 'break_name', label: 'Break', flex: 1.22 },
  { key: 'side', label: 'Side', flex: 0.42 },
  { key: 'completed', label: 'Completed', flex: 0.62 },
  { key: 'weapon', label: 'Weapon', flex: 0.72 },
  { key: 'material_type', label: 'Material', flex: 0.78 },
  { key: 'quantity', label: 'Qty', flex: 0.38 },
  { key: 'size_inches', label: 'Size (in)', flex: 0.48 },
  { key: 'total_size_inches', label: 'Total (in)', flex: 0.52 },
];

const COMPLETED_CAMERA_COLOR = '#2e7d32';
const FILTER_ACTIVE_COLOR = '#d4af37';

function normalizeGradeBreakRow(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: row.break_id ?? row.id,
    video_link: normalizeUserBreakVideoLinks(row.video_link),
    user_break_id: row.user_break_id ?? null,
  };
}

function normalizeCourseBreakRow(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: row.break_id ?? row.id,
    video_link: normalizeUserBreakVideoLinks(row.video_link),
    user_break_id: row.user_break_id ?? null,
  };
}

function getUploadSuccessToastMessage(student) {
  const name = student?.student_name || student?.username || 'Student';
  return `The uploaded break has been successful for ${name}`;
}

const FAIL_TOAST = 'The upload was not successful, please try again.';

function formatBreakCell(b, key) {
  const v = b?.[key];
  if (v == null || v === '') return '—';
  if (key === 'size_inches') {
    const n = Number(v);
    if (Number.isFinite(n)) return String(Math.round(n));
  }
  return String(v);
}

/** Postgres `text[]` / JSON — match My Breaks */
function normalizeCourseNames(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p.filter(Boolean).map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function formatYouTubeTitle(breakItem, student) {
  const studentName = student?.student_name || student?.username || 'Student';
  const side = breakItem.side || '';
  const breakName = breakItem.break_name || '';
  const quantity = breakItem.quantity || 1;
  const material = breakItem.material_type || '';
  const sizeInches = breakItem.size_inches || '';
  let materialStr = '';
  if (material.toLowerCase() === 'wood') {
    materialStr = sizeInches ? `${sizeInches}" wood` : 'wood';
  } else if (
    material.toLowerCase() === 'concrete' ||
    material.toLowerCase() === 'concrete block'
  ) {
    materialStr = sizeInches ? `${sizeInches}" concrete block` : 'concrete block';
  } else {
    materialStr = material || '';
  }
  return `${studentName} - ${side} ${breakName} - ${quantity} ${materialStr}`
    .replace(/\s+/g, ' ')
    .trim();
}

function UploadGradeHeaderRow({ styles, isDarkMode, tableInnerStyle }) {
  const headerBg = isDarkMode
    ? styles.syllabusHeaderRowBgDark
    : styles.syllabusHeaderRowBgLight;
  const headerTextStyle = isDarkMode
    ? styles.syllabusHeaderTextDark
    : styles.syllabusHeaderTextLight;
  return (
    <View style={[styles.syllabusHeaderRow, tableInnerStyle, headerBg]}>
      {GRADE_TABLE_COLUMNS.map((col) => (
        <Text
          key={col.key}
          style={[styles.syllabusHeaderCell, { flex: col.flex }, headerTextStyle]}
          numberOfLines={2}
        >
          {col.label}
        </Text>
      ))}
    </View>
  );
}

function UploadGradeDataRow({
  breakItem,
  alt,
  onPress,
  onPressCompletedVideo,
  styles,
  isDarkMode,
  tableInnerStyle,
}) {
  const rowStyle = alt ? styles.tableRowAlt : styles.tableRowBase;
  const textStyle = isDarkMode ? styles.breakTextNoUrlDark : styles.breakTextNoUrlLight;
  const showCompleted =
    breakItem?.user_break_id != null && hasUserBreakVideos(breakItem?.video_link);
  const videoCount = normalizeUserBreakVideoLinks(breakItem?.video_link).length;
  return (
    <Pressable
      style={({ pressed }) => [
        styles.syllabusDataRow,
        tableInnerStyle,
        rowStyle,
        pressed && styles.tableRowPressed,
      ]}
      onPress={() => onPress(breakItem)}
      {...(Platform.OS === 'web'
        ? { dataSet: { uploadBreakRow: alt ? 'alt' : 'base' } }
        : {})}
    >
      {GRADE_TABLE_COLUMNS.map((col) => {
        if (col.key === 'completed') {
          return (
            <View
              key={col.key}
              style={[styles.syllabusCell, styles.completedCell, { flex: col.flex }]}
              {...(Platform.OS === 'web'
                ? { dataSet: { uploadCompletedCell: 'true' } }
                : {})}
            >
              {showCompleted ? (
                <TouchableOpacity
                  style={styles.completedCellButton}
                  onPress={(e) => {
                    if (Platform.OS === 'web') {
                      e?.stopPropagation?.();
                      e?.preventDefault?.();
                    }
                    onPressCompletedVideo(breakItem);
                  }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  accessibilityRole="button"
                  accessibilityLabel={`Play completed video for ${breakItem.break_name}`}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { uploadCompletedCell: 'true' } }
                    : {})}
                >
                  <MaterialIcons
                    name="videocam"
                    size={26}
                    color={COMPLETED_CAMERA_COLOR}
                  />
                  {videoCount > 1 ? (
                    <Text style={styles.videoCountBadge}>{videoCount}</Text>
                  ) : null}
                </TouchableOpacity>
              ) : null}
            </View>
          );
        }
        return (
          <Text
            key={col.key}
            style={[styles.syllabusCell, { flex: col.flex }, styles.breakRowText, textStyle]}
            numberOfLines={4}
          >
            {formatBreakCell(breakItem, col.key)}
          </Text>
        );
      })}
    </Pressable>
  );
}

function CourseBreakFilterDropdown({
  label,
  value,
  options,
  open,
  onToggle,
  onClose,
  onSelect,
  styles,
}) {
  const displayValue = value != null && value !== '' ? `(${value})` : '';
  return (
    <View
      style={styles.filterContainer}
      {...(Platform.OS === 'web'
        ? { dataSet: { uploadCourseBreakFilter: 'true' } }
        : {})}
    >
      <TouchableOpacity
        style={[styles.filterButton, value != null && value !== '' && styles.filterButtonActive]}
        onPress={onToggle}
      >
        <Text
          style={[
            styles.filterButtonText,
            value != null && value !== '' && styles.filterButtonTextActive,
          ]}
          {...(Platform.OS === 'web'
            ? { dataSet: { uploadCourseBreakFilterText: 'true' } }
            : {})}
        >
          {label} {displayValue}
        </Text>
        <Text style={styles.filterDropdownArrow}>{open ? '▲' : '▼'}</Text>
      </TouchableOpacity>
      {open ? (
        <>
          {Platform.OS === 'web' ? (
            <TouchableOpacity
              style={styles.filterDropdownOverlay}
              activeOpacity={1}
              onPress={onClose}
            />
          ) : null}
          <View
            style={styles.filterDropdown}
            {...(Platform.OS === 'web'
              ? { dataSet: { uploadCourseBreakFilterMenu: 'true' } }
              : {})}
          >
            <ScrollView style={styles.filterDropdownScroll} nestedScrollEnabled>
              <TouchableOpacity
                style={styles.filterOption}
                onPress={() => onSelect(null)}
              >
                <Text
                  style={styles.filterOptionText}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { uploadCourseBreakFilterMenuText: 'true' } }
                    : {})}
                >
                  All
                </Text>
              </TouchableOpacity>
              {options.map((option) => (
                <TouchableOpacity
                  key={String(option)}
                  style={[
                    styles.filterOption,
                    value === option && styles.filterOptionSelected,
                  ]}
                  onPress={() => onSelect(option)}
                >
                  <Text
                    style={styles.filterOptionText}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { uploadCourseBreakFilterMenuText: 'true' } }
                      : {})}
                  >
                    {option}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </>
      ) : null}
    </View>
  );
}

function UploadCourseBreakRow({
  breakItem,
  onPress,
  onPressCompletedVideo,
  styles,
}) {
  const showVideo =
    breakItem?.user_break_id != null && hasUserBreakVideos(breakItem?.video_link);
  const videoCount = normalizeUserBreakVideoLinks(breakItem?.video_link).length;
  const weaponTitle = formatCourseBreakWeaponTitle(breakItem);
  const metaLine = formatCourseBreakMetaLine(breakItem);
  return (
    <Pressable
      style={({ pressed }) => [
        styles.courseBreakRow,
        pressed && styles.tableRowPressed,
      ]}
      onPress={() => onPress(breakItem)}
      {...(Platform.OS === 'web'
        ? { dataSet: { uploadCourseBreakRow: 'true' } }
        : {})}
    >
      <View style={styles.courseBreakRowInner}>
        <View style={styles.courseBreakRowTextCol}>
          {weaponTitle ? (
            <Text style={styles.courseBreakWeaponTitle}>{weaponTitle}</Text>
          ) : null}
          {metaLine ? (
            <Text style={styles.courseBreakMeta}>{metaLine}</Text>
          ) : null}
        </View>
        {showVideo ? (
          <TouchableOpacity
            style={styles.courseBreakVideoButton}
            onPress={(e) => {
              if (Platform.OS === 'web') {
                e?.stopPropagation?.();
                e?.preventDefault?.();
              }
              onPressCompletedVideo(breakItem);
            }}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel={`Play completed video for ${weaponTitle || breakItem.break_name || 'break'}`}
            {...(Platform.OS === 'web'
              ? { dataSet: { uploadCourseBreakVideoBtn: 'true' } }
              : {})}
          >
            <MaterialIcons name="videocam" size={26} color={COMPLETED_CAMERA_COLOR} />
            {videoCount > 1 ? (
              <Text style={styles.videoCountBadge}>{videoCount}</Text>
            ) : null}
          </TouchableOpacity>
        ) : (
          <View style={styles.courseBreakVideoPlaceholder} />
        )}
      </View>
    </Pressable>
  );
}

function GradeBreaksTableSection({
  gradeName,
  breaks,
  styles,
  isDarkMode,
  windowWidth,
  onPressRow,
  onPressCompletedVideo,
}) {
  const horizontalScrollRef = useRef(null);
  const [hScrollX, setHScrollX] = useState(0);
  const [hContentW, setHContentW] = useState(0);
  const [hViewportW, setHViewportW] = useState(0);
  const [hTrackW, setHTrackW] = useState(0);

  const tableInnerWidth = Math.max(TABLE_MIN_WIDTH, windowWidth - 48);
  const tableInnerStyle = { width: tableInnerWidth, alignSelf: 'flex-start' };
  const maxHScroll =
    hViewportW > 0 ? Math.max(0, hContentW - hViewportW) : 0;

  const scrollHorizontalBy = useCallback(
    (dir) => {
      if (maxHScroll <= 0) return;
      const step = Math.max(80, Math.round(hViewportW * 0.35));
      const next = Math.max(
        0,
        Math.min(maxHScroll, hScrollX + (dir > 0 ? step : -step))
      );
      horizontalScrollRef.current?.scrollTo?.({ x: next, animated: true });
    },
    [maxHScroll, hScrollX, hViewportW]
  );

  const handleHorizontalTrackPress = useCallback(
    (e) => {
      const x = e.nativeEvent.locationX;
      if (maxHScroll <= 0 || hTrackW <= 0) return;
      const ratio = Math.max(0, Math.min(1, x / hTrackW));
      horizontalScrollRef.current?.scrollTo?.({
        x: ratio * maxHScroll,
        animated: true,
      });
    },
    [maxHScroll, hTrackW]
  );

  const thumbW =
    maxHScroll > 0 && hTrackW > 0 && hContentW > 0
      ? Math.max(40, Math.min(hTrackW, (hViewportW / hContentW) * hTrackW))
      : 48;
  const thumbLeft =
    maxHScroll > 0 && hTrackW > 0 && thumbW > 0
      ? (hScrollX / maxHScroll) * Math.max(0, hTrackW - thumbW)
      : 0;

  return (
    <View style={styles.section}>
      <View
        style={styles.gradeHeaderBlock}
        {...(Platform.OS === 'web'
          ? { dataSet: { uploadGradeIconBlock: 'true' } }
          : {})}
      >
        <Image
          source={getGradeBeltIconSource(gradeName)}
          style={styles.gradeIconLarge}
          contentFit="contain"
          transition={0}
        />
        <Text style={styles.gradeTitleCentered}>{gradeName}</Text>
      </View>
      <ScrollView
        ref={horizontalScrollRef}
        horizontal
        nestedScrollEnabled
        showsHorizontalScrollIndicator
        scrollEventThrottle={16}
        onScroll={(ev) => setHScrollX(ev.nativeEvent.contentOffset.x)}
        onContentSizeChange={(w) => setHContentW(w)}
        onLayout={(ev) => setHViewportW(ev.nativeEvent.layout.width)}
        style={styles.syllabusHorizontalScroll}
        contentContainerStyle={styles.syllabusHorizontalScrollContent}
        {...(Platform.OS === 'web'
          ? {
              className:
                'records-list-horizontal-scroll syllabus-table-horizontal-scroll',
            }
          : {})}
      >
        <View style={styles.syllabusTableCard}>
          <UploadGradeHeaderRow
            styles={styles}
            isDarkMode={isDarkMode}
            tableInnerStyle={tableInnerStyle}
          />
          {breaks.length === 0 ? (
            <Text style={styles.emptySection}>No breaks for this grade.</Text>
          ) : (
            breaks.map((b, ri) => (
              <UploadGradeDataRow
                key={b.id ?? b.break_id ?? ri}
                breakItem={b}
                alt={ri % 2 === 0}
                onPress={onPressRow}
                onPressCompletedVideo={onPressCompletedVideo}
                styles={styles}
                isDarkMode={isDarkMode}
                tableInnerStyle={tableInnerStyle}
              />
            ))
          )}
        </View>
      </ScrollView>
      {maxHScroll > 0 && (
        <View
          style={styles.hScrollBar}
          {...(Platform.OS === 'web'
            ? { dataSet: { uploadScrollbar: 'true' } }
            : {})}
        >
          <TouchableOpacity
            style={styles.hScrollArrow}
            onPress={() => scrollHorizontalBy(-1)}
            accessibilityLabel="Scroll table left"
          >
            <Text style={styles.hScrollArrowText}>◀</Text>
          </TouchableOpacity>
          <Pressable
            style={styles.hScrollTrack}
            onLayout={(ev) => setHTrackW(ev.nativeEvent.layout.width)}
            onPress={handleHorizontalTrackPress}
          >
            <View
              pointerEvents="none"
              style={[styles.hScrollThumb, { width: thumbW, left: thumbLeft }]}
            />
          </Pressable>
          <TouchableOpacity
            style={styles.hScrollArrow}
            onPress={() => scrollHorizontalBy(1)}
            accessibilityLabel="Scroll table right"
          >
            <Text style={styles.hScrollArrowText}>▶</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

export default function UploadNewBreaksPage({ navigation, route }) {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const styles = getStyles(theme);
  const stopwatchRef = useRef(null);
  const pickedWebFileRef = useRef(null);
  const pickedUriRef = useRef(null);
  const pickedFileSizeRef = useRef(null);
  const pickedMimeTypeRef = useRef(null);
  const webFileInputRef = useRef(null);
  const youtubeOAuthRef = useRef(null);

  const clearPickedVideo = () => {
    pickedWebFileRef.current = null;
    pickedUriRef.current = null;
    pickedFileSizeRef.current = null;
    pickedMimeTypeRef.current = null;
    setPickedFileName('');
  };

  const setPickedVideoFromAsset = (asset) => {
    const name =
      asset.fileName ||
      (asset.uri && String(asset.uri).split('/').pop()) ||
      'video.mp4';
    const sizeMsg = assertVideoUploadable({
      fileSize: asset.fileSize,
      base64: asset.base64,
    });
    if (sizeMsg) {
      setUploadModalError(sizeMsg);
      clearPickedVideo();
      return false;
    }
    pickedWebFileRef.current = null;
    pickedUriRef.current = asset.uri || null;
    pickedFileSizeRef.current = asset.fileSize ?? null;
    pickedMimeTypeRef.current = asset.mimeType || 'video/mp4';
    setPickedFileName(name);
    return true;
  };

  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);

  const [activeTab, setActiveTab] = useState('grade');

  const [grades, setGrades] = useState([]);
  const [selectedGradeName, setSelectedGradeName] = useState(null);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [gradeBreaks, setGradeBreaks] = useState([]);
  const [loadingGradeBreaks, setLoadingGradeBreaks] = useState(false);

  const [allCourses, setAllCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [courseBreaks, setCourseBreaks] = useState([]);
  const [loadingCourseBreaks, setLoadingCourseBreaks] = useState(false);
  const [courseWeaponFilter, setCourseWeaponFilter] = useState(null);
  const [courseMaterialFilter, setCourseMaterialFilter] = useState(null);
  const [courseQuantityFilter, setCourseQuantityFilter] = useState(null);
  const [courseWeaponFilterOpen, setCourseWeaponFilterOpen] = useState(false);
  const [courseMaterialFilterOpen, setCourseMaterialFilterOpen] = useState(false);
  const [courseQuantityFilterOpen, setCourseQuantityFilterOpen] = useState(false);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadBreak, setUploadBreak] = useState(null);
  const [pickedFileName, setPickedFileName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [uploadModalError, setUploadModalError] = useState('');
  const [uploadModalSuccess, setUploadModalSuccess] = useState('');
  const [completedVideoBreak, setCompletedVideoBreak] = useState(null);
  const pickingFileRef = useRef(false);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      const uid = route?.params?.userId ?? route?.params?.user?.id;
      if (route?.params?.user) {
        setCurrentUser(route.params.user);
        return () => {
          cancelled = true;
        };
      }
      if (uid) {
        fetch(`${getApiUrl()}/api/users/${uid}`)
          .then((r) => r.json())
          .then((d) => {
            if (!cancelled && d?.id) setCurrentUser(d);
          })
          .catch(() => {});
      }
      return () => {
        cancelled = true;
      };
    }, [route?.params?.user, route?.params?.userId])
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingStudents(true);
      try {
        const requesterId = currentUser?.id;
        const requesterQ =
          requesterId != null
            ? `&requester_id=${encodeURIComponent(String(requesterId))}`
            : '';
        const res = await fetch(`${getApiUrl()}/api/users?visible=true${requesterQ}`);
        if (!res.ok) throw new Error('students');
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) {
          setStudents(data);
          const selfId = currentUser?.id;
          if (selfId && data.some((s) => s.id === selfId)) {
            setSelectedStudentId(selfId);
          } else if (data.length === 1) {
            setSelectedStudentId(data[0].id);
          }
        }
      } catch {
        if (!cancelled) setStudents([]);
      } finally {
        if (!cancelled) setLoadingStudents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  useEffect(() => {
    fetch(`${getApiUrl()}/api/grades`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) =>
        setGrades(
          Array.isArray(data)
            ? data.filter((g) => g?.grade_name && !SYLLABUS_OMIT_GRADE_NAMES.has(g.grade_name))
            : []
        )
      )
      .catch(() => setGrades([]));
  }, []);

  useEffect(() => {
    fetch(`${getApiUrl()}/api/courses`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) =>
        setAllCourses(Array.isArray(data) ? dedupeCoursesForDropdown(data) : [])
      )
      .catch(() => setAllCourses([]));
  }, []);

  const selectedStudent = students.find((s) => s.id === selectedStudentId);

  useEffect(() => {
    if (!selectedStudent) {
      setEnrolledCourses([]);
      setSelectedCourse(null);
      setCourseBreaks([]);
      return;
    }
    const enrolledNames = normalizeCourseNames(selectedStudent.courses);
    const superUserAll =
      hasSuperUserAccess(selectedStudent) && enrolledNames.length === 0;
    const list = superUserAll
      ? allCourses
      : allCourses.filter((c) => enrolledNames.includes(c.course_name));
    setEnrolledCourses(list);
    setSelectedCourse((prev) =>
      prev && list.some((c) => c.id === prev.id) ? prev : null
    );
  }, [selectedStudent, allCourses]);

  const fetchGradeBreaksForStudent = useCallback(async () => {
    if (!selectedGradeName || !selectedStudentId) {
      setGradeBreaks([]);
      return;
    }
    setLoadingGradeBreaks(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(selectedGradeName)}/student/${selectedStudentId}`
      );
      if (!res.ok) throw new Error('grade breaks');
      const data = await res.json();
      setGradeBreaks(
        Array.isArray(data) ? data.map((row) => normalizeGradeBreakRow(row)) : []
      );
    } catch {
      setGradeBreaks([]);
    } finally {
      setLoadingGradeBreaks(false);
    }
  }, [selectedGradeName, selectedStudentId]);

  useEffect(() => {
    void fetchGradeBreaksForStudent();
  }, [fetchGradeBreaksForStudent]);

  const fetchCourseBreaksForStudent = useCallback(async () => {
    if (!selectedCourse?.id || !selectedStudentId) {
      setCourseBreaks([]);
      return;
    }
    setLoadingCourseBreaks(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/breaks/course/${encodeURIComponent(String(selectedCourse.id))}/student/${encodeURIComponent(String(selectedStudentId))}`
      );
      if (!res.ok) throw new Error('course breaks');
      const data = await res.json();
      setCourseBreaks(
        Array.isArray(data) ? data.map((row) => normalizeCourseBreakRow(row)) : []
      );
    } catch {
      setCourseBreaks([]);
    } finally {
      setLoadingCourseBreaks(false);
    }
  }, [selectedCourse?.id, selectedStudentId]);

  useEffect(() => {
    void fetchCourseBreaksForStudent();
  }, [fetchCourseBreaksForStudent]);

  useEffect(() => {
    setCourseWeaponFilter(null);
    setCourseMaterialFilter(null);
    setCourseQuantityFilter(null);
    setCourseWeaponFilterOpen(false);
    setCourseMaterialFilterOpen(false);
    setCourseQuantityFilterOpen(false);
  }, [selectedCourse?.id]);

  const courseBreakFilterOptions = useMemo(() => {
    const weapons = [
      ...new Set(courseBreaks.map((b) => b.weapon).filter(Boolean)),
    ].sort();
    const materials = [
      ...new Set(courseBreaks.map((b) => b.material_type).filter(Boolean)),
    ].sort();
    const quantities = [
      ...new Set(
        courseBreaks
          .map((b) => b.quantity)
          .filter((q) => q != null && q !== '')
      ),
    ].sort((a, b) => Number(a) - Number(b));
    return { weapons, materials, quantities };
  }, [courseBreaks]);

  const filteredCourseBreaks = useMemo(() => {
    let list = courseBreaks;
    if (courseWeaponFilter) {
      list = list.filter((b) => b.weapon === courseWeaponFilter);
    }
    if (courseMaterialFilter) {
      list = list.filter((b) => b.material_type === courseMaterialFilter);
    }
    if (courseQuantityFilter != null) {
      list = list.filter(
        (b) => Number(b.quantity) === Number(courseQuantityFilter)
      );
    }
    return list;
  }, [
    courseBreaks,
    courseWeaponFilter,
    courseMaterialFilter,
    courseQuantityFilter,
  ]);

  const closeCourseBreakFilterMenus = useCallback(() => {
    setCourseWeaponFilterOpen(false);
    setCourseMaterialFilterOpen(false);
    setCourseQuantityFilterOpen(false);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!uploadModalOpen) return;
    void youtubeOAuthRef.current?.refresh?.();
  }, [uploadModalOpen]);

  const openUploadModal = (b) => {
    if (!selectedStudentId) {
      setToast({
        type: 'error',
        message: 'Please select a student before uploading.',
      });
      return;
    }
    clearPickedVideo();
    setUploadModalError('');
    setUploadModalSuccess('');
    setUploadBreak(b);
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    if (saving) return;
    pickingFileRef.current = false;
    setUploadModalOpen(false);
    setUploadBreak(null);
    clearPickedVideo();
    setUploadModalError('');
    setUploadModalSuccess('');
  };

  const launchGalleryPick = async () => {
    setUploadModalError('');
    if (Platform.OS === 'web') {
      pickingFileRef.current = true;
      if (typeof window !== 'undefined') {
        const resetPickingGuard = () => {
          window.removeEventListener('focus', resetPickingGuard);
          setTimeout(() => {
            pickingFileRef.current = false;
          }, 350);
        };
        window.addEventListener('focus', resetPickingGuard);
      }
      webFileInputRef.current?.click?.();
      return;
    }
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) {
      setUploadModalError(
        'Permission required to choose a video from your library.'
      );
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync(getExerciseVideoPickerOptions());
    if (result.canceled || !result.assets?.[0]) return;
    setPickedVideoFromAsset(result.assets[0]);
  };

  const onWebFileChange = (e) => {
    const file = e.target?.files?.[0];
    pickingFileRef.current = false;
    if (!file) return;
    setUploadModalError('');
    const sizeMsg = assertVideoUploadable({ fileSize: file.size });
    if (sizeMsg) {
      setUploadModalError(sizeMsg);
      clearPickedVideo();
      e.target.value = '';
      return;
    }
    pickedWebFileRef.current = file;
    pickedUriRef.current = null;
    pickedFileSizeRef.current = file.size;
    pickedMimeTypeRef.current = file.type || 'video/mp4';
    setPickedFileName(file.name || 'video');
    e.target.value = '';
    setTimeout(() => {
      pickingFileRef.current = false;
    }, 400);
  };

  const hasPickedVideo = () =>
    Boolean(pickedWebFileRef.current || pickedUriRef.current);

  const handleSubmitUpload = async () => {
    if (!selectedStudentId || !uploadBreak) {
      setUploadModalError(FAIL_TOAST);
      return;
    }
    if (!hasPickedVideo()) {
      setUploadModalError('Please choose a video file with Upload first.');
      return;
    }

    const breakId = resolveBreakId(uploadBreak);
    if (breakId == null) {
      setUploadModalError(FAIL_TOAST);
      return;
    }

    const student = students.find((s) => s.id === selectedStudentId);
    setSaving(true);
    setUploadModalError('');
    setUploadModalSuccess('');

    try {
      const { titleSuffix } = await fetchExistingUserBreakMeta(
        selectedStudentId,
        breakId
      );

      const youtubeTitle = formatYouTubeTitle(uploadBreak, student) + titleSuffix;
      const youtubeUrl = await uploadYoutubeVideoFile({
        uri: pickedUriRef.current,
        webFile: pickedWebFileRef.current,
        fileName: pickedFileName || 'video.mp4',
        mimeType: pickedMimeTypeRef.current || 'video/mp4',
        fileSize: pickedFileSizeRef.current,
        title: youtubeTitle,
        description: `Break: ${uploadBreak.break_name}`,
        privacyStatus: 'unlisted',
        isShort: true,
      });

      const completedDate = new Date().toISOString().split('T')[0];

      const saveData = await saveUserBreakWithVideo({
        studentId: selectedStudentId,
        breakId,
        youtubeUrl,
        completedDate,
      });

      const videoCount = normalizeUserBreakVideoLinks(saveData.video_link).length;
      clearPickedVideo();
      setUploadModalError('');
      setUploadModalSuccess(
        videoCount > 1
          ? `${getUploadSuccessToastMessage(student)} (${videoCount} videos linked to this break.)`
          : getUploadSuccessToastMessage(student)
      );

      if (activeTab === 'grade' && selectedGradeName) {
        await fetchGradeBreaksForStudent();
      } else if (activeTab === 'course' && selectedCourse?.id) {
        await fetchCourseBreaksForStudent();
      }

      setTimeout(() => {
        setUploadModalOpen(false);
        setUploadBreak(null);
        setUploadModalSuccess('');
      }, 2800);
    } catch (e) {
      const msg = e?.message || FAIL_TOAST;
      if (isYoutubeReconnectError(msg)) {
        youtubeOAuthRef.current?.markNeedsReconnect?.(msg);
        setUploadModalError(msg);
      } else if (isYoutubeUploadLimitError(msg)) {
        setUploadModalSuccess('');
        setUploadModalError(msg);
      } else {
        setUploadModalOpen(false);
        setUploadBreak(null);
        clearPickedVideo();
        setToast({ type: 'error', message: msg });
      }
    } finally {
      setSaving(false);
    }
  };

  const openCompletedVideo = (breakItem) => {
    if (!hasUserBreakVideos(breakItem?.video_link)) return;
    setCompletedVideoBreak(breakItem);
  };

  const closeCompletedVideo = () => setCompletedVideoBreak(null);

  const gradeLabel = selectedGradeName || 'Select Grade…';
  const courseLabel = selectedCourse?.course_name || 'Select Course…';

  const navUser = currentUser;
  const navUserId = navUser?.id;

  return (
    <View
      style={styles.container}
      {...(Platform.OS === 'web'
        ? { dataSet: { screen: 'upload-new-breaks' } }
        : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu
            navigation={navigation}
            stopwatchRef={stopwatchRef}
            currentUser={navUser}
          />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Upload New Breaks</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={navUser} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        nestedScrollEnabled
      >
        <Text style={styles.fieldLabel}>Student</Text>
        <View
          nativeID="upload-student-dropdown-trigger"
          style={styles.dropdownTriggerShell}
          {...(Platform.OS === 'web'
            ? { dataSet: { uploadStudentDropdown: 'trigger' } }
            : {})}
        >
          <TouchableOpacity
            style={styles.dropdownTriggerInner}
            onPress={() => setStudentPickerOpen(true)}
            activeOpacity={0.7}
          >
            <Text style={styles.dropdownTriggerText}>
              {loadingStudents
                ? 'Loading students…'
                : selectedStudent
                  ? selectedStudent.student_name ||
                    selectedStudent.username ||
                    `Student #${selectedStudent.id}`
                  : 'Select student'}
            </Text>
            <Text style={styles.dropdownChevron}>▼</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.tabsContainer}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'grade' && styles.activeTab]}
            onPress={() => setActiveTab('grade')}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'grade' && styles.activeTabText,
              ]}
            >
              Grade Breaks
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'course' && styles.activeTab]}
            onPress={() => setActiveTab('course')}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'course' && styles.activeTabText,
              ]}
            >
              Course Breaks
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'other' && styles.activeTab]}
            onPress={() => setActiveTab('other')}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'other' && styles.activeTabText,
              ]}
            >
              Other Breaks
            </Text>
          </TouchableOpacity>
        </View>

        {activeTab === 'grade' && (
          <View style={styles.tabContent}>
            {!selectedStudentId ? (
              <Text style={styles.hintText}>
                Select a student to choose grade breaks.
              </Text>
            ) : (
              <>
                <View
                  nativeID="upload-grade-dropdown-trigger"
                  style={styles.dropdownTriggerShell}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { uploadGradeDropdown: 'trigger' } }
                    : {})}
                >
                  <TouchableOpacity
                    style={styles.dropdownTriggerInner}
                    onPress={() => setGradeModalOpen(true)}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.dropdownTriggerText}>{gradeLabel}</Text>
                    <Text style={styles.dropdownChevron}>▼</Text>
                  </TouchableOpacity>
                </View>
                {loadingGradeBreaks ? (
                  <ActivityIndicator
                    style={{ marginTop: 24 }}
                    color={theme.colors.primary}
                  />
                ) : selectedGradeName ? (
                  <GradeBreaksTableSection
                    gradeName={selectedGradeName}
                    breaks={gradeBreaks}
                    styles={styles}
                    isDarkMode={theme.isDarkMode}
                    windowWidth={windowWidth}
                    onPressRow={openUploadModal}
                    onPressCompletedVideo={openCompletedVideo}
                  />
                ) : null}
              </>
            )}
          </View>
        )}

        {activeTab === 'course' && (
          <View style={styles.tabContent}>
            {!selectedStudentId ? (
              <Text style={styles.hintText}>
                Select a student to see their courses.
              </Text>
            ) : enrolledCourses.length === 0 ? (
              <Text style={styles.hintText}>
                No enrolled courses for this student.
              </Text>
            ) : (
              <>
                <View
                  nativeID="upload-course-dropdown-trigger"
                  style={styles.dropdownTriggerShell}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { uploadCourseDropdown: 'trigger' } }
                    : {})}
                >
                  <TouchableOpacity
                    style={styles.dropdownTriggerInner}
                    onPress={() => setCourseModalOpen(true)}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.dropdownTriggerText}>{courseLabel}</Text>
                    <Text style={styles.dropdownChevron}>▼</Text>
                  </TouchableOpacity>
                </View>
                {loadingCourseBreaks ? (
                  <ActivityIndicator
                    style={{ marginTop: 24 }}
                    color={theme.colors.primary}
                  />
                ) : selectedCourse ? (
                  <>
                    {courseBreaks.length > 0 ? (
                      <View
                        style={styles.courseBreakFiltersRow}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { uploadCourseBreakFilters: 'true' } }
                          : {})}
                      >
                        <CourseBreakFilterDropdown
                          label="Weapon"
                          value={courseWeaponFilter}
                          options={courseBreakFilterOptions.weapons}
                          open={courseWeaponFilterOpen}
                          onToggle={() => {
                            setCourseWeaponFilterOpen((prev) => !prev);
                            setCourseMaterialFilterOpen(false);
                            setCourseQuantityFilterOpen(false);
                          }}
                          onClose={closeCourseBreakFilterMenus}
                          onSelect={(value) => {
                            setCourseWeaponFilter(value);
                            closeCourseBreakFilterMenus();
                          }}
                          styles={styles}
                        />
                        <CourseBreakFilterDropdown
                          label="Quantity"
                          value={courseQuantityFilter}
                          options={courseBreakFilterOptions.quantities}
                          open={courseQuantityFilterOpen}
                          onToggle={() => {
                            setCourseQuantityFilterOpen((prev) => !prev);
                            setCourseWeaponFilterOpen(false);
                            setCourseMaterialFilterOpen(false);
                          }}
                          onClose={closeCourseBreakFilterMenus}
                          onSelect={(value) => {
                            setCourseQuantityFilter(value);
                            closeCourseBreakFilterMenus();
                          }}
                          styles={styles}
                        />
                        <CourseBreakFilterDropdown
                          label="Material"
                          value={courseMaterialFilter}
                          options={courseBreakFilterOptions.materials}
                          open={courseMaterialFilterOpen}
                          onToggle={() => {
                            setCourseMaterialFilterOpen((prev) => !prev);
                            setCourseWeaponFilterOpen(false);
                            setCourseQuantityFilterOpen(false);
                          }}
                          onClose={closeCourseBreakFilterMenus}
                          onSelect={(value) => {
                            setCourseMaterialFilter(value);
                            closeCourseBreakFilterMenus();
                          }}
                          styles={styles}
                        />
                      </View>
                    ) : null}
                    <View style={styles.courseBreakList}>
                      {courseBreaks.length === 0 ? (
                        <Text style={styles.emptySection}>
                          No breaks in this course.
                        </Text>
                      ) : filteredCourseBreaks.length === 0 ? (
                        <Text style={styles.emptySection}>
                          No breaks match the selected filters.
                        </Text>
                      ) : (
                        filteredCourseBreaks.map((b) => (
                          <UploadCourseBreakRow
                            key={b.id ?? b.break_id}
                            breakItem={b}
                            onPress={openUploadModal}
                            onPressCompletedVideo={openCompletedVideo}
                            styles={styles}
                          />
                        ))
                      )}
                    </View>
                  </>
                ) : null}
              </>
            )}
          </View>
        )}

        {activeTab === 'other' && (
          <View style={styles.tabContent}>
            <TouchableOpacity
              style={styles.primaryLinkButton}
              onPress={() =>
                navigation.navigate('CreateBreaksBuilder', {
                  userId: navUserId,
                  user: navUser,
                })
              }
            >
              <Text style={styles.primaryLinkButtonText}>
                Open Create Break Builder
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {toast ? (
          <View style={styles.pageToastSlot}>
            <View
              style={[
                styles.toastBanner,
                toast.type === 'success' ? styles.toastSuccess : styles.toastError,
              ]}
            >
              <Text style={styles.toastBannerText}>{toast.message}</Text>
            </View>
          </View>
        ) : null}
      </ScrollView>

      {/* Student picker */}
      <Modal
        visible={studentPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setStudentPickerOpen(false)}
      >
        <View style={styles.gradeModalRoot}>
          <Pressable
            style={styles.gradeModalBackdrop}
            onPress={() => setStudentPickerOpen(false)}
          />
          <View style={styles.dropdownSheet}>
            <Text style={styles.dropdownSheetTitle}>Select student</Text>
            <ScrollView
              style={styles.dropdownScroll}
              contentContainerStyle={styles.dropdownScrollContent}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
            >
              {students.map((s) => (
                <TouchableOpacity
                  key={s.id}
                  style={styles.dropdownRow}
                  onPress={() => {
                    setSelectedStudentId(s.id);
                    setStudentPickerOpen(false);
                  }}
                >
                  <Text style={styles.dropdownRowText}>
                    {s.student_name || s.username || `User ${s.id}`}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Grade picker */}
      <Modal
        visible={gradeModalOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setGradeModalOpen(false)}
      >
        <View style={styles.gradeModalRoot}>
          <Pressable
            style={styles.gradeModalBackdrop}
            onPress={() => {
              Keyboard.dismiss();
              setGradeModalOpen(false);
            }}
          />
          <View
            style={styles.dropdownSheet}
            {...(Platform.OS === 'web'
              ? { dataSet: { uploadGradeModal: 'true' } }
              : {})}
          >
            <Text style={styles.dropdownSheetTitle}>Grade</Text>
            <ScrollView
              style={styles.dropdownScroll}
              contentContainerStyle={styles.dropdownScrollContent}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
            >
              {grades.map((g) => (
                <TouchableOpacity
                  key={g.id}
                  style={styles.gradePickerRow}
                  onPress={() => {
                    setSelectedGradeName(g.grade_name);
                    setGradeModalOpen(false);
                  }}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { uploadGradeRow: 'true' } }
                    : {})}
                >
                  <Text style={styles.dropdownRowText}>{g.grade_name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Course picker */}
      <Modal
        visible={courseModalOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setCourseModalOpen(false)}
      >
        <View style={styles.gradeModalRoot}>
          <Pressable
            style={styles.gradeModalBackdrop}
            onPress={() => setCourseModalOpen(false)}
          />
          <View style={styles.dropdownSheet}>
            <Text style={styles.dropdownSheetTitle}>Course</Text>
            <ScrollView
              style={styles.dropdownScroll}
              contentContainerStyle={styles.dropdownScrollContent}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
            >
              {enrolledCourses.map((c) => (
                <TouchableOpacity
                  key={c.id}
                  style={styles.dropdownRow}
                  onPress={() => {
                    setSelectedCourse(c);
                    setCourseModalOpen(false);
                  }}
                >
                  <Text style={styles.dropdownRowText}>{c.course_name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Upload modal */}
      <Modal
        visible={uploadModalOpen}
        transparent
        animationType="fade"
        onRequestClose={closeUploadModal}
      >
        <View style={styles.uploadModalRoot}>
          <Pressable
            style={styles.gradeModalBackdrop}
            onPress={() => {
              if (pickingFileRef.current) return;
              closeUploadModal();
            }}
          />
          <View style={styles.uploadModalSheet}>
            <Text style={styles.uploadBreakModalTitle}>Upload Break</Text>
            <Text style={styles.modalHint}>
              {uploadBreak?.break_name || ''}
            </Text>

            <YoutubeOAuthBar ref={youtubeOAuthRef} variant="upload-break-modal" />

            {Platform.OS === 'web' &&
              React.createElement('input', {
                ref: (el) => {
                  webFileInputRef.current = el;
                },
                type: 'file',
                accept: 'video/*',
                style: { display: 'none' },
                onChange: onWebFileChange,
                onClick: (ev) => {
                  ev.stopPropagation();
                },
              })}

            <TouchableOpacity
              style={styles.fileButton}
              onPress={(e) => {
                if (Platform.OS === 'web' && e?.stopPropagation) {
                  e.stopPropagation();
                }
                void launchGalleryPick();
              }}
              disabled={saving}
            >
              <Text style={styles.fileButtonText}>Upload</Text>
            </TouchableOpacity>

            <TextInput
              style={styles.readOnlyFileInput}
              value={pickedFileName}
              editable={false}
              placeholder="No file selected"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
            />

            {uploadModalSuccess ? (
              <View style={[styles.toastBanner, styles.toastSuccess, styles.uploadModalErrorBanner]}>
                <Text style={styles.toastBannerText}>{uploadModalSuccess}</Text>
              </View>
            ) : null}

            {uploadModalError ? (
              <View style={[styles.toastBanner, styles.toastError, styles.uploadModalErrorBanner]}>
                <Text style={styles.toastBannerText}>{uploadModalError}</Text>
              </View>
            ) : null}

            <View style={styles.editModalActions}>
              <TouchableOpacity
                style={[styles.modalBtn, styles.modalBtnCancel]}
                onPress={closeUploadModal}
                disabled={saving}
              >
                <Text style={styles.modalBtnCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalBtn,
                  styles.modalBtnSave,
                  saving && { opacity: 0.6 },
                ]}
                onPress={handleSubmitUpload}
                disabled={saving}
              >
                <Text style={styles.modalBtnSaveText}>
                  {saving ? 'Working…' : 'Submit'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <UserBreakVideosModal
        visible={completedVideoBreak != null}
        onClose={closeCompletedVideo}
        title={
          completedVideoBreak?.break_name
            ? `${completedVideoBreak.break_name}${
                normalizeUserBreakVideoLinks(completedVideoBreak.video_link).length > 1
                  ? ` (${normalizeUserBreakVideoLinks(completedVideoBreak.video_link).length} videos)`
                  : ''
              }`
            : 'Break video'
        }
        videoLinks={completedVideoBreak?.video_link}
        overlayStyle={styles.videoModalOverlay}
        contentStyle={styles.videoModalContent}
      />

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => {
  const gold = theme.colors.primary || '#d4af37';
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 16,
      paddingHorizontal: 20,
    },
    hamburgerContainer: { marginRight: 15 },
    breadcrumbContainer: { flex: 1 },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {},
    content: { flex: 1 },
    contentContainer: { padding: 20, paddingBottom: 48 },
    fieldLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
    },
    dropdownTriggerShell: {
      borderRadius: 10,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#F0F0F0',
      borderWidth: theme.isDarkMode ? 1 : 0,
      borderColor: theme.isDarkMode ? '#444444' : 'transparent',
      marginBottom: 16,
      overflow: 'hidden',
    },
    dropdownTriggerInner: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 14,
      backgroundColor: 'transparent',
    },
    dropdownTriggerText: {
      fontSize: 16,
      flex: 1,
      color: theme.isDarkMode ? '#FFFFFF' : '#111111',
      fontWeight: '500',
      backgroundColor: 'transparent',
    },
    dropdownChevron: {
      fontSize: 14,
      color: theme.isDarkMode ? '#FFFFFF' : '#111111',
      backgroundColor: 'transparent',
    },
    tabsContainer: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      marginBottom: 8,
    },
    tab: {
      flex: 1,
      paddingVertical: 14,
      alignItems: 'center',
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
    },
    activeTab: {
      borderBottomColor: theme.colors.primary || '#d4af37',
    },
    tabText: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.6,
      textAlign: 'center',
    },
    activeTabText: {
      opacity: 1,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    tabContent: {
      paddingVertical: 16,
    },
    hintText: {
      color: theme.colors.text,
      opacity: 0.85,
      fontSize: 15,
      marginTop: 8,
    },
    gradeModalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    gradeModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    dropdownSheet: {
      width: '100%',
      maxWidth: 440,
      backgroundColor: theme.isDarkMode ? '#2c2c2c' : '#fff',
      borderRadius: 12,
      padding: 16,
      maxHeight: '80%',
      zIndex: 2,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }
        : { elevation: 8 }),
    },
    dropdownSheetTitle: {
      fontSize: 16,
      fontWeight: '700',
      marginBottom: 10,
      color: theme.colors.text,
    },
    dropdownScroll: {
      maxHeight: 360,
      backgroundColor: 'transparent',
    },
    dropdownScrollContent: {
      paddingBottom: 8,
    },
    dropdownRow: {
      paddingVertical: 14,
      paddingHorizontal: 12,
      marginBottom: 4,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? '#3d3d3d' : '#f2f2f2',
      borderWidth: theme.isDarkMode ? 1 : 0,
      borderColor: theme.isDarkMode ? '#555555' : 'transparent',
    },
    /** Grade picker modal only: black tiles on dark grey tray; grey rows in light mode */
    gradePickerRow: {
      paddingVertical: 14,
      paddingHorizontal: 12,
      marginBottom: 4,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? '#000000' : '#f2f2f2',
      borderWidth: theme.isDarkMode ? 1 : 0,
      borderColor: theme.isDarkMode ? '#555555' : 'transparent',
    },
    dropdownRowText: {
      fontSize: 16,
      color: theme.isDarkMode ? '#FFFFFF' : '#111111',
      fontWeight: '500',
      backgroundColor: 'transparent',
    },
    section: { marginBottom: 28 },
    gradeHeaderBlock: {
      alignItems: 'center',
      marginBottom: 16,
    },
    gradeIconLarge: {
      width: 168,
      height: 168,
      alignSelf: 'center',
      borderRadius: 14,
      marginBottom: 12,
    },
    gradeTitleCentered: {
      fontSize: 22,
      fontWeight: '700',
      color: theme.colors.heading,
      textAlign: 'center',
    },
    syllabusHeaderRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 10,
      paddingHorizontal: 6,
    },
    syllabusHeaderRowBgDark: {
      backgroundColor: '#1e1e1e',
      borderBottomWidth: 2,
      borderBottomColor: '#FDFD0A',
    },
    syllabusHeaderRowBgLight: {
      backgroundColor: '#ececec',
      borderBottomWidth: 2,
      borderBottomColor: '#c9a227',
    },
    syllabusHeaderCell: {
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      paddingRight: 4,
    },
    syllabusHeaderTextDark: { color: '#FDFD0A' },
    syllabusHeaderTextLight: { color: '#8b6914' },
    syllabusDataRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 11,
      paddingHorizontal: 6,
      borderWidth: 0,
    },
    syllabusCell: {
      fontSize: 13,
      paddingRight: 4,
      minWidth: 0,
    },
    syllabusHorizontalScroll: {
      width: '100%',
      alignSelf: 'stretch',
      flexGrow: 0,
      marginHorizontal: -4,
      ...(Platform.OS === 'web' && { overflowX: 'auto' }),
    },
    syllabusHorizontalScrollContent: {
      paddingBottom: 8,
    },
    hScrollBar: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'nowrap',
      alignSelf: 'stretch',
      paddingVertical: 10,
      paddingHorizontal: 12,
      marginTop: 6,
      borderTopWidth: 1,
      borderTopColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
      borderRadius: 10,
      backgroundColor: theme.isDarkMode ? '#141414' : '#EEEEEE',
      width: '100%',
      gap: 10,
    },
    hScrollArrow: {
      width: 36,
      height: 36,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
      backgroundColor: theme.isDarkMode ? '#262626' : '#E8E8E8',
    },
    hScrollArrowText: {
      fontSize: 13,
      lineHeight: 15,
      color: theme.isDarkMode ? '#FFFFFF' : '#000000',
      fontWeight: '700',
    },
    hScrollTrack: {
      flex: 1,
      flexShrink: 1,
      minWidth: 56,
      height: 12,
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#333333' : '#D0D0D0',
      position: 'relative',
      overflow: 'hidden',
    },
    hScrollThumb: {
      position: 'absolute',
      top: 0,
      height: 12,
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#FFFF00' : gold,
    },
    syllabusTableCard: {
      borderRadius: 10,
      overflow: 'hidden',
      borderWidth: theme.isDarkMode ? 1 : StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
    },
    tableRowBase: {
      backgroundColor: theme.isDarkMode ? '#252525' : '#FFFFFF',
    },
    tableRowAlt: {
      backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
    },
    tableRowPressed: {
      opacity: 0.88,
    },
    breakRowText: {
      opacity: 1,
      backgroundColor: 'transparent',
    },
    breakTextNoUrlDark: { color: '#FFFFFF' },
    breakTextNoUrlLight: { color: '#0d0d0d' },
    emptySection: {
      fontStyle: 'italic',
      color: theme.colors.text,
      opacity: 0.7,
      paddingVertical: 8,
    },
    courseBreakList: { marginTop: 12 },
    courseBreakFiltersRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
      marginTop: 16,
      zIndex: 20,
      backgroundColor: 'transparent',
    },
    filterContainer: {
      position: 'relative',
      zIndex: 1000,
    },
    filterButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderWidth: 1,
      borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
      minWidth: 120,
    },
    filterButtonActive: {
      backgroundColor: FILTER_ACTIVE_COLOR,
      borderColor: FILTER_ACTIVE_COLOR,
    },
    filterButtonText: {
      fontSize: 14,
      fontWeight: '500',
      color: theme.isDarkMode ? theme.colors.text : '#000000',
      flex: 1,
      backgroundColor: 'transparent',
    },
    filterButtonTextActive: {
      color: '#000000',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    filterDropdownArrow: {
      fontSize: 12,
      color: theme.isDarkMode ? theme.colors.text : '#000000',
      marginLeft: 8,
      backgroundColor: 'transparent',
    },
    filterDropdownOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      zIndex: 9998,
      ...(Platform.OS !== 'web' ? { position: 'absolute' } : {}),
    },
    filterDropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: 4,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      borderWidth: 1,
      borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
      borderRadius: 8,
      maxHeight: 200,
      zIndex: 9999,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.5,
            shadowRadius: 8,
            elevation: 10,
          }),
    },
    filterDropdownScroll: {
      maxHeight: 200,
    },
    filterOption: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.isDarkMode ? theme.colors.border : '#F0F0F0',
      backgroundColor: 'transparent',
    },
    filterOptionSelected: {
      backgroundColor: theme.isDarkMode
        ? 'rgba(212, 175, 55, 0.15)'
        : 'rgba(212, 175, 55, 0.2)',
    },
    filterOptionText: {
      fontSize: 14,
      color: theme.isDarkMode ? theme.colors.text : '#000000',
      backgroundColor: 'transparent',
    },
    courseBreakRow: {
      paddingVertical: 14,
      paddingHorizontal: 12,
      borderRadius: 10,
      marginBottom: 8,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#f5f5f5',
      borderWidth: theme.isDarkMode ? 1 : 0,
      borderColor: theme.isDarkMode ? '#444' : 'transparent',
    },
    courseBreakRowInner: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    courseBreakRowTextCol: {
      flex: 1,
      minWidth: 0,
      paddingRight: 8,
      backgroundColor: 'transparent',
    },
    courseBreakVideoButton: {
      width: 40,
      height: 40,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      position: 'relative',
    },
    courseBreakVideoPlaceholder: {
      width: 40,
      height: 40,
      backgroundColor: 'transparent',
    },
    courseBreakWeaponTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.primary || '#d4af37',
      backgroundColor: 'transparent',
    },
    courseBreakMeta: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.85,
      marginTop: 4,
      backgroundColor: 'transparent',
    },
    primaryLinkButton: {
      backgroundColor: theme.colors.primary || '#d4af37',
      paddingVertical: 16,
      paddingHorizontal: 24,
      borderRadius: 12,
      alignItems: 'center',
      alignSelf: 'center',
      marginTop: 16,
    },
    primaryLinkButtonText: {
      color: '#000',
      fontWeight: '700',
      fontSize: 16,
    },
    uploadModalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    uploadModalSheet: {
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#FFFFFF',
      borderRadius: 12,
      padding: 20,
      maxWidth: 520,
      width: '100%',
      zIndex: 2,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }
        : { elevation: 8 }),
    },
    uploadModalErrorBanner: {
      marginTop: 12,
    },
    uploadBreakModalTitle: {
      fontSize: 20,
      fontWeight: '700',
      textAlign: 'center',
      marginBottom: 12,
      color: theme.isDarkMode ? '#FDFD0A' : '#8b6914',
    },
    modalHint: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.85,
      marginBottom: 12,
      textAlign: 'center',
    },
    fileButton: {
      backgroundColor: theme.colors.primary || '#d4af37',
      padding: 14,
      borderRadius: 8,
      alignItems: 'center',
      marginTop: 8,
    },
    fileButtonText: { color: '#000', fontWeight: '700' },
    readOnlyFileInput: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      padding: 12,
      marginTop: 12,
      color: theme.colors.text,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FAFAFA',
    },
    editModalActions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 12,
      marginTop: 20,
    },
    modalBtn: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 8 },
    modalBtnCancel: { backgroundColor: '#666' },
    modalBtnSave: { backgroundColor: theme.colors.primary || '#d4af37' },
    modalBtnCancelText: { color: '#fff', fontWeight: '600' },
    modalBtnSaveText: { color: '#000', fontWeight: '700' },
    toastBanner: {
      paddingVertical: 12,
      paddingHorizontal: 14,
      borderRadius: 8,
    },
    toastSuccess: {
      backgroundColor: '#2e7d32',
    },
    toastError: {
      backgroundColor: '#c62828',
    },
    toastBannerText: {
      color: '#FFFFFF',
      fontSize: 14,
      fontWeight: '600',
      textAlign: 'center',
    },
    completedCell: {
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 40,
      backgroundColor: 'transparent',
    },
    completedCellButton: {
      backgroundColor: 'transparent',
      position: 'relative',
      alignItems: 'center',
      justifyContent: 'center',
    },
    videoCountBadge: {
      position: 'absolute',
      top: -2,
      right: -4,
      minWidth: 16,
      height: 16,
      paddingHorizontal: 4,
      borderRadius: 8,
      backgroundColor: '#2e7d32',
      color: '#FFFFFF',
      fontSize: 10,
      fontWeight: '700',
      textAlign: 'center',
      overflow: 'hidden',
    },
    pageToastSlot: {
      marginTop: 20,
      width: '100%',
    },
    videoModalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.65)',
      justifyContent: 'center',
      padding: 20,
    },
    videoModalContent: {
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      borderRadius: 12,
      padding: 16,
      maxWidth: 720,
      width: '100%',
      alignSelf: 'center',
    },
    videoModalClose: {
      alignSelf: 'flex-end',
      padding: 8,
    },
    videoModalCloseText: {
      fontSize: 22,
      color: theme.colors.text,
      fontWeight: '600',
    },
    videoModalTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 12,
    },
    youtubeContainer: {
      width: '100%',
      aspectRatio: 16 / 9,
      backgroundColor: '#000',
      borderRadius: 8,
      overflow: 'hidden',
    },
    youtubeIframe: {
      width: '100%',
      height: '100%',
      border: 'none',
    },
    youtubeWebView: {
      flex: 1,
      width: '100%',
      minHeight: 220,
    },
  });
};
